import { asDetailLevel, DEFAULT_DETAIL, DETAIL, type DetailLevel } from './detail';
import { applySaverOverride, type PowerSavers, type SaverOverride } from './powerSavers';

// The render settings (D186; retiered D187) — what the main menu's Settings panel changes,
// and the one place the stage reads them from.
//
// This is a MODULE-LEVEL value on purpose, and it is worth saying why rather than leaving it
// to be discovered. The stage is built deep inside two flows (hotseatFlow, netFlow) that
// already declare the stage seam three different ways, and threading a settings object down
// both would mean editing those three declarations for this. A single readable snapshot,
// applied when a stage is BUILT, keeps the seam untouched — and since every one of these
// knobs only takes effect on a fresh stage anyway (geometry is loaded once, the shadow map is
// configured once), "applied at build" is the honest contract rather than a shortcut.
//
// Changing a setting therefore does nothing until the board is rebuilt — so D191 makes the
// flows rebuild it, and `onRenderSettingsChange` is how they hear about it. `fps` and `stats`
// are the exceptions: they are DOM overlays the frame loop owns, and they apply live.

export interface RenderSettings {
  /** Graphics tier: which rung of the decimation ladder, and whether the sun casts. */
  detail: DetailLevel;
  /**
   * Show a small frame-rate counter on the board (D190).
   *
   * LIVE, with no rebuild — the stage re-reads it each sampling window and mounts or drops
   * the counter — because it is pure DOM and there is no reason to make somebody restart a
   * board to see a number. (`detail` and `props` reach a board in play too, but only by
   * rebuilding it — D191.)
   */
  fps: boolean;
  /**
   * D249: does the board react to the pointer — the flock looking over and walking across,
   * the wheat rattling, the trees bending under a drag (D232/D234/D235)?
   *
   * LIVE, like `fps` and unlike everything else here: the scene reads it each frame at the
   * two places that consume the gaze, so switching it off settles what is already leaning
   * rather than freezing it mid-lean, and switching it back on picks up on the next move.
   * That is also why it is not a rebuild key — a board torn down to stop a sheep looking at
   * you would be a worse answer than the question.
   */
  liveBoard: boolean;
  /**
   * H22 (D335): look straight down at a map of the board, drawn once and composited. Applies
   * LIVE, through the glide — never a rebuild. Default OFF, and only ever the player's choice.
   */
  mapView: boolean;
  /** Draw the scatter props at all. A developer A/B, not a shipping mode. */
  props: boolean;
  /** Show the live draw-call / triangle / frame-time readout over the board. */
  stats: boolean;
}

export const defaultRenderSettings = (): RenderSettings => ({
  detail: DEFAULT_DETAIL,
  fps: false,
  liveBoard: true,
  mapView: false,
  props: true,
  stats: false,
});

/**
 * Whether the player's tier asks for shadows.
 *
 * Read through a function rather than off the settings object because the DEVICE has the
 * final say — the phone budget allocates no shadow map at all — and a boolean sitting in
 * `RenderSettings` would read as the whole answer when it is only half of it (stage.ts ANDs
 * this with the device tier). One accessor, one place to look.
 */
export function wantsShadows(s: RenderSettings): boolean {
  return DETAIL[s.detail].shadows;
}

let current: RenderSettings = defaultRenderSettings();

/**
 * D294: the URL's `?savers=` override, set once at boot. Kept beside the settings rather than
 * in them because it is a measuring tool, never something the Settings panel stores.
 */
let saverOverride: SaverOverride = {};
export function setSaverOverride(o: SaverOverride): void {
  saverOverride = o;
}

/** D294: the power savers for these settings — the tier's set, then the URL override. */
export function powerSavers(s: RenderSettings = current): PowerSavers {
  return applySaverOverride(DETAIL[s.detail].savers, saverOverride);
}

/** The settings a stage built right now would use. */
export function renderSettings(): RenderSettings {
  return current;
}

/**
 * Which fields a live stage cannot absorb without being built again (D191).
 *
 * `detail` decides which `.glb`s were fetched and whether the sun casts — both settled at
 * build. `props` decides whether 531 InstancedMeshes were adopted at all. Neither can be
 * walked back on a scene that already exists, so changing either means a rebuild.
 *
 * `fps` and `stats` are NOT here: they are DOM overlays the frame loop mounts and drops on
 * its own, so they apply live and must never cost anybody a rebuild mid-turn. Nor is
 * `liveBoard` (D249): the scene reads it per frame at the two places that consume the
 * pointer, so it settles rather than freezing and needs no new geometry either way.
 * Nor is `mapView` (H22): the stage reads it per frame and GLIDES there — the board it shows
 * is the same scene, photographed, so nothing has to be fetched or built again.
 */
const REBUILD_KEYS = ['detail', 'props'] as const;

export interface RenderSettingsChange {
  next: RenderSettings;
  /** True when a live stage must be torn down and built again to honour this change. */
  needsRebuild: boolean;
}

type Listener = (change: RenderSettingsChange) => void;
const listeners = new Set<Listener>();

/**
 * Subscribe to changes. The flows use this to rebuild the board when the graphics tier moves
 * mid-game — before D191 a tier change simply did nothing until you opened a new board, which
 * is not what a Settings panel reachable from the PAUSE menu can reasonably mean.
 */
export function onRenderSettingsChange(fn: Listener): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/** Replace the snapshot and tell anyone who is holding a stage what it costs them. */
export function setRenderSettings(next: RenderSettings): void {
  const prev = current;
  current = next;
  const needsRebuild = REBUILD_KEYS.some((k) => prev[k] !== next[k]);
  // A copy of the set: a listener that unsubscribes itself mid-notify must not shorten the
  // iteration and skip its neighbour.
  for (const fn of [...listeners]) fn({ next, needsRebuild });
}

/**
 * Read overrides off the URL, so a phone can be pointed at a tier without a UI round trip
 * and a bug report can carry its own repro. `?detail=fine&fps=1&stats=1&props=0&liveBoard=0`.
 *
 * Unknown values resolve to the default rather than throwing — a mistyped query string
 * should give you the game, not a blank page.
 */
export function renderSettingsFromQuery(search: string, base: RenderSettings = defaultRenderSettings()): RenderSettings {
  const q = new URLSearchParams(search);
  const flag = (name: string, fallback: boolean): boolean => {
    const raw = q.get(name);
    if (raw === null) return fallback;
    return raw !== '0' && raw !== 'false';
  };
  return {
    detail: q.has('detail') ? asDetailLevel(q.get('detail')) : base.detail,
    fps: flag('fps', base.fps),
    liveBoard: flag('liveBoard', base.liveBoard),
    mapView: flag('mapView', base.mapView),
    props: flag('props', base.props),
    stats: flag('stats', base.stats),
  };
}
